import {DialogFooter, Form, FormButton, FormField, type FormControl} from 'platform/components';
import {Grid, HStack, Show, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {type FeatureResponseBody, type TrimFeatureAssignment} from '@dms/api/new-vehicle';
import {getNewVehicleEnumLabel} from '@dms/features/new-vehicle';

import type {Nullish} from 'shared/web';

import {handleCatalogueError, optionalText, t} from './catalogueUi';
import {GrossPriceField} from './GrossPriceField';
import {useYearFeatureNames} from './hooks/useYearFeatureNames';
import {
  optionalMoneyInput,
  optionalPriceError,
  optionPriceRequired,
  priceInput,
} from './moneyInput';
import {NewPricelistVersionDialog} from './NewPricelistVersionDialog';
import {NumberField} from './NumberField';
import {PriceRow} from './PriceRow';
import {ITEM_CHANGE_CANCELLED, type PricedItemChangeOutcome} from './types/PricedItemChangeOutcome';
import {usePublishedPriceSave} from './usePublishedPriceSave';

const assignmentSchema = z
  .object({
    inclusion: z.enum(['STANDARD', 'OPTIONAL']),
    priceNet: optionalText,
    costNet: optionalMoneyInput,
    sortOrder: z.coerce.number().int().nonnegative(),
    anyOfVariantIds: z.array(z.string()),
    allOfFeatureIds: z.array(z.string()),
  })
  .superRefine((values, context) => {
    if (values.inclusion !== 'OPTIONAL') {
      return;
    }
    const message =
      values.priceNet === '' ? optionPriceRequired() : optionalPriceError(values.priceNet);
    if (isNotNil(message)) {
      context.addIssue({code: 'custom', path: ['priceNet'], message});
    }
  })
  .transform((values) =>
    values.inclusion === 'OPTIONAL'
      ? {...values, priceNet: priceInput.parse(values.priceNet)}
      : values
  );
export type AssignmentValues = z.infer<typeof assignmentSchema>;

function AssignmentPriceRow(props: {
  control: FormControl<AssignmentValues>;
  vatRate: string | Nullish;
  canViewCost: boolean;
}) {
  const inclusion = useWatch({control: props.control, name: 'inclusion'});
  return (
    <PriceRow
      vatRate={props.vatRate}
      canViewCost={props.canViewCost}
      hasPrice={inclusion === 'OPTIONAL'}
      spacing={3}
      span={2}
    >
      <Show when={inclusion === 'OPTIONAL'}>
        <GrossPriceField
          control={props.control}
          name="priceNet"
          vatRate={props.vatRate}
          label={t('entity.newVehicle.catalogue.priceNet', 'Price net')}
          isRequired
        />
      </Show>
      <Show when={props.canViewCost}>
        <NumberField
          control={props.control}
          name="costNet"
          label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
        />
      </Show>
    </PriceRow>
  );
}

export function AssignmentDialog(props: {
  modelYearId: string;
  assignment: TrimFeatureAssignment;
  /** The model year's items, not the matrix rows added from the library and not saved yet. */
  yearFeatures: FeatureResponseBody[];
  variants: {id: string; orderCode: string}[];
  canViewCost: boolean;
  vatRate: string | Nullish;
  /** The trim is a Draft trim, outside the price list: its price and inclusion save directly. */
  isTrimOutsidePricelist: boolean;
  /** Resolves ITEM_CHANGE_CANCELLED when the new pricelist version of an inclusion switch was cancelled. */
  save: (values: AssignmentValues) => Promise<PricedItemChangeOutcome>;
  onDone: () => void;
}) {
  const publishedPrice = usePublishedPriceSave(props.modelYearId);
  const defaultValues: AssignmentValues = {
    inclusion: props.assignment.inclusion,
    priceNet: props.assignment.priceNet ?? '',
    costNet: props.assignment.costNet ?? '',
    sortOrder: props.assignment.sortOrder,
    anyOfVariantIds: props.assignment.appliesIf.anyOfVariantIds,
    allOfFeatureIds: props.assignment.appliesIf.allOfFeatureIds,
  };
  const conditionNames = useYearFeatureNames(props.yearFeatures, defaultValues.allOfFeatureIds);
  const priceTargetId = props.assignment.priceTargetId;
  const submit = async (values: AssignmentValues) => {
    // Only an Optional price that stays Optional has a price to adjust. An inclusion switch is the
    // trim's write, which asks a published year for a new pricelist version (D102).
    const saving: Promise<unknown> =
      values.inclusion === 'OPTIONAL' && isNotNil(priceTargetId)
        ? publishedPrice.save({
            values,
            storedValues: defaultValues,
            priceField: 'priceNet',
            scope: 'trimFeature',
            resourceId: priceTargetId,
            isDraftItem: props.isTrimOutsidePricelist,
            save: props.save,
            onDone: props.onDone,
          })
        : props.save(values).then((outcome) => {
            if (outcome !== ITEM_CHANGE_CANCELLED) {
              props.onDone();
            }
          });
    await saving.catch(handleCatalogueError);
  };
  return (
    <>
      <Form<AssignmentValues>
        experimentalZodSchema={assignmentSchema}
        defaultValues={defaultValues}
        onSubmit={submit}
      >
        {(control) => (
          <VStack spacing={4}>
            <Grid columns={2} spacing={3}>
              <FormField
                control={control}
                name="inclusion"
                type="choice"
                menuInPortal
                label={t('entity.newVehicle.catalogue.inclusion', 'Inclusion')}
                options={['STANDARD', 'OPTIONAL'].map((value) => ({
                  value,
                  label: getNewVehicleEnumLabel('inclusion', value, t),
                }))}
              />
              <FormField
                control={control}
                name="sortOrder"
                type="number"
                label={t('entity.newVehicle.catalogue.sortOrder', 'Sort order')}
              />
              <AssignmentPriceRow
                control={control}
                vatRate={props.vatRate}
                canViewCost={props.canViewCost}
              />
              <FormField
                control={control}
                name="anyOfVariantIds"
                type="multiChoice"
                menuInPortal
                label={t('entity.newVehicle.catalogue.appliesVariants', 'Applies to variants')}
                options={props.variants.map(({id, orderCode}) => ({value: id, label: orderCode}))}
              />
              <FormField
                control={control}
                name="allOfFeatureIds"
                type="multiChoice"
                menuInPortal
                label={t(
                  'entity.newVehicle.catalogue.appliesFeatures',
                  'Applies when features selected'
                )}
                options={conditionNames.features.map(({id, name}) => ({value: id, label: name}))}
                isLoading={conditionNames.isLoading}
                isDisabled={conditionNames.isLoading || conditionNames.isError}
                errorMessage={
                  conditionNames.isError
                    ? t(
                        'entity.newVehicle.catalogue.outsideEquipmentNamesFailed',
                        'Equipment this model year no longer uses could not be loaded, so this field cannot be changed. Reload the page to try again.'
                      )
                    : undefined
                }
              />
            </Grid>
            <DialogFooter>
              <HStack justify="flex-end">
                <FormButton
                  control={control}
                  type="button"
                  title={t('general.actions.save', 'Save')}
                  onClick={() => void control.submitForm()}
                />
              </HStack>
            </DialogFooter>
          </VStack>
        )}
      </Form>
      <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
    </>
  );
}
